import DeleteIcon from "@mui/icons-material/Delete";
import CheckIcon from "@mui/icons-material/Check";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import RateReviewIcon from "@mui/icons-material/RateReview";

import PaperFragment from "../../../../helper-components/misc/PaperFragment";
import StackedHeader from "../../../../helper-components/misc/StackedHeader";
import DataGridTable from "../../../../helper-components/table/DataGridTable";

import HighlightRequestReviewForm from "./HighlightRequestReviewForm";

import { isAdmin } from "../../../../helper-functions/shared/miscUtils";
import { columns, defaultTableOptions } from "../../../../helper-functions/table/highlights";
import {
    deleteHighlights,
    approveHighlights,
    disapproveHighlights,
    completeHighlights,
    queryHighlights,
    queryHighlightsRequiringReview
} from "../../../../helper-functions/api/highlightQueries";

import { getLanguage } from "../../../../config/language/language";

/**
 * Table showing highlights.
 * If no data found, show error message instead.
 */
export default function HighlightsTable(props) {
    const highlights = props.backend.highlights;

    const showDeleteDialog = () => {
        props.showDialog({
            title: "Delete highlight(s)",
            message: `Are you sure you want to delete the ${highlights.selected.length} selected highlight(s)?`,
            actionTitle: "Delete",
            actionColor: "error",
            callback: (confirm) => handleDelete(confirm)
        });
    };

    const showApproveDialog = () => {
        props.showDialog({
            title: "Approve highlight(s)",
            message: `Are you sure you want to approve the ${highlights.selected.length} selected highlight(s)?`,
            actionTitle: "Approve",
            actionColor: "success",
            callback: (confirm) => handleApprove(confirm)
        });
    };

    const showDisapproveDialog = () => {
        props.showDialog({
            title: "Disapprove highlight(s)",
            message: `Are you sure you want to disapprove the ${highlights.selected.length} selected highlight(s)?`,
            actionTitle: "Disapprove",
            actionColor: "error",
            callback: (confirm) => handleDisapprove(confirm)
        });
    };

    const showCompleteDialog = () => {
        props.showDialog({
            title: "Complete highlight(s)",
            message: `Are you sure you want to complete the ${highlights.selected.length} selected highlight(s)?
            Only complete the highlights which have been successfully reviewed and created as new clips.`,
            actionTitle: "Complete",
            actionColor: "success",
            callback: (confirm) => handleComplete(confirm)
        });
    };

    const showReviewDialog = () => {
        props.showDialog({
            title: "Begin review process?",
            message: `This will find the most popular highlight candidates that require your attention.

            You will be able to vote on each highlight alongside adding an optional comment. Would you like to begin the process?`,
            component: <HighlightRequestReviewForm {...props} callback={(confirm, options) => handleReview(confirm, options)} />
        });
    };

    const handleDelete = (confirm) => {
        if (!confirm) return;

        const ids = highlights.selected.map((request) => request.id);
        deleteHighlights(props, { body: { ids }, ids });
    };

    const handleApprove = (confirm) => {
        if (!confirm) return;

        const ids = highlights.selected.map((request) => request.id);
        approveHighlights(props, { body: { ids } });
    };

    const handleDisapprove = (confirm) => {
        if (!confirm) return;

        const ids = highlights.selected.map((request) => request.id);
        disapproveHighlights(props, { body: { ids } });
    };

    const handleComplete = (confirm) => {
        if (!confirm) return;

        const ids = highlights.selected.map((request) => request.id);
        completeHighlights(props, { body: { ids } });
    };

    const handleReview = (confirm, options = {}) => {
        props.setOpenDialog(false); // Manually close the dialog.
        if (!confirm) return;

        queryHighlightsRequiringReview(props, options, (response) => {
            if (!response || response?.body?.length === 0) {
                props.showSnackbar({ message: "No highlights requiring review found.", color: "info" });
                return;
            }

            props.backend.setHighlightReviewQueue(response.body);
            const firstHighlight = response.body[0];

            props.showSnackbar({ message: `${response.body?.length} highlight(s) loaded for review.`, color: "success" });
            props.browser.navigate(`/highlights/view/${firstHighlight.id}`);
        });
    };

    return (
        <PaperFragment
            className="tour-highlight-reviews-2 tour-highlight-reviews-3"
            title={
                <StackedHeader
                    {...props}
                    title="Highlights"
                    subtitle="View and manage highlights."
                    icons={[
                        {
                            icon: <DeleteIcon />,
                            tooltip: "Delete selected highlights",
                            disabled: highlights.selected.length === 0 || highlights.selected.some((r) => ["IN_PROGRESS"].includes(r.status)),
                            onClick: showDeleteDialog
                        },
                        {
                            icon: <ThumbUpIcon />,
                            tooltip: "Force-approve selected highlights",
                            onClick: showApproveDialog,
                            visible: () => isAdmin(props),
                            disabled: highlights.selected.length === 0 || highlights.selected.some((r) => r.status === "APPROVED")
                        },
                        {
                            icon: <ThumbDownIcon />,
                            tooltip: "Force-disapprove selected highlights",
                            onClick: showDisapproveDialog,
                            visible: () => isAdmin(props),
                            disabled: highlights.selected.length === 0 || highlights.selected.some((r) => r.status === "APPROVED")
                        },
                        {
                            icon: <CheckIcon />,
                            tooltip: "Complete selected highlights",
                            onClick: showCompleteDialog,
                            visible: () => isAdmin(props),
                            disabled: highlights.selected.length === 0 || highlights.selected.some((r) => r.status === "APPROVED")
                        }
                    ]}
                    buttons={[
                        {
                            icon: <RateReviewIcon />,
                            title: "Review highlights",
                            onClick: showReviewDialog
                        }
                    ]}
                />
            }
            value={
                <DataGridTable
                    {...props}
                    {...defaultTableOptions}
                    rowHeight={60}
                    states={highlights}
                    columns={columns(props)}
                    query={(options) => queryHighlights(props, options)}
                    empty={getLanguage().NO_HIGHLIGHT_FOUND}
                    refresh={10}
                />
            }
        />
    );
}
